import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReviewList } from '../../molecules/ReviewList/ReviewList';
import { ReviewListFilter } from '../../molecules/ReviewListFilter/ReviewListFilter';
import { GetReviewsResponseDto } from '@bitecritic-api/src/reviews/dto/get-reviews.dto';
import { reviewsService } from '~/services/reviewsService/reviews.service';
import { ReviewSortBy, ReviewListFilterValues, SortOrder } from '~/types/reviews';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { useLocale } from '~/hooks/useLocale';

interface ReviewListContainerProps {
  foodId?: number;
  placeId?: string;
  userId?: string;
  defaultFilters?: ReviewListFilterValues;
}

export const ReviewListContainer: React.FC<ReviewListContainerProps> = ({
  foodId,
  placeId,
  userId,
  defaultFilters,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(1);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [filters, setFilters] = React.useState<ReviewListFilterValues>({
    ...defaultFilters,
    foodId,
    placeId,
    userId,
    sortBy: ReviewSortBy.CREATED_AT,
    sortOrder: SortOrder.DESC,
  });
  const { currentLocale } = useLocale();

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [filters]);

  const { data, isLoading, error } = useQuery<GetReviewsResponseDto>({
    queryKey: ['reviews', { ...filters, page, locale: currentLocale.code }],
    queryFn: () =>
      reviewsService.getReviews({
        ...filters,
        page,
        startDate: filters.startDate ? new Date(filters.startDate) : undefined,
        endDate: filters.endDate ? new Date(filters.endDate) : undefined,
        sortOrder: filters.sortOrder as SortOrder,
      }),
  });

  const handleApplyFilters = (newFilters: ReviewListFilterValues) => {
    setFilters((current: ReviewListFilterValues) => ({
      ...newFilters,
      sortBy: current.sortBy,
      sortOrder: current.sortOrder,
    }));
    setIsFilterOpen(false);
  };

  const handleSortChange = (sortBy: ReviewSortBy, sortOrder: SortOrder) => {
    setFilters((current: ReviewListFilterValues) => ({
      ...current,
      sortBy,
      sortOrder,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="w-full">
      {/* Filter Drawer */}
      <Drawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        placement="right"
        size="sm"
        classNames={{
          base: "rounded-l-lg",
          header: "border-b-1",
          body: "py-6",
        }}
      >
        <DrawerContent>
          <DrawerHeader className="flex flex-col gap-1">
            <h3 className="text-xl font-semibold">{t('reviews.filters.title')}</h3>
          </DrawerHeader>
          <DrawerBody>
            <ReviewListFilter
              onApply={handleApplyFilters}
              defaultValues={filters}
              onSortChange={handleSortChange}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Reviews */}
      <div>
        {error ? (
          <div className="text-center text-red-500">
            Error loading reviews. Please try again.
          </div>
        ) : isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg" />
            ))}
          </div>
        ) : data ? (
          <ReviewList
            reviews={data.reviews}
            total={data.total}
            page={page}
            limit={data.limit}
            onPageChange={handlePageChange}
            filters={filters}
            onFilterClick={() => setIsFilterOpen(true)}
            onSortChange={handleSortChange}
            showPlaceCard={!placeId}
            showUserCard={!userId}
            showFoodCard={!foodId}
          />
        ) : null}
      </div>
    </div>
  );
}; 